import moment from 'moment';
import { formatBuddhistEra } from 'src/app/shared/pipes/thai-datetime.pipe';
import {
    map as _map,
    toNumber as _toNumber,
    toString as _toString,
} from 'lodash';
import {
    DeliveryNoteItem,
    DeliveryNoteParameter,
} from 'src/app/shared/services/print-sheet/interfaces/delivery-note-param';
import {
    SellerEntity,
    toSellerPrintBlock,
} from 'src/app/shared/config/entity-config';
import {
    DeliveryItemInfo,
    DeliveryNoteInput,
    inputMomentFormat,
} from './delivery-note-input';

// The builder that turns the temporary-delivery-note form state into the
// jsreport `delivery-note` page. Pure so the spec beside this file can pin the
// built object — in particular that no price or VAT field can ever appear on
// this document, which is what lets a non-VAT-registered entity issue it.

// The template has a single heading line, so the English subtitle lives in the
// same string. The form's free-text override wins when non-empty.
export const TEMP_DELIVERY_NOTE_TITLE =
    'ใบส่งของชั่วคราว (Temporary Delivery Note)';

export const formatDisplayDate = (input: string, abb?: boolean): string => {
    if (input) {
        return formatBuddhistEra(
            moment(input, inputMomentFormat),
            abb ? 'DD/MM/YYYY' : 'DD MMMM YYYY',
        );
    }
    return '';
};

const formatString = (input: any): string => {
    return _toString(input).trim();
};

// Takes the ENTITY rather than a bare print block, matching
// buildInvoiceSaveRequest: the branding decision below needs to know WHICH
// entity is selling, not just what it prints.
//
// The jsreport template's letterhead logo and signature image are the
// company's own assets. Printing them above another seller's legal name would
// misattribute the document, so any seller that is not the company prints
// without them and signs the blank box by hand.
export const buildDeliveryNoteParam = (
    input: DeliveryNoteInput,
    entity: SellerEntity,
): DeliveryNoteParameter => {
    const seller = toSellerPrintBlock(entity);
    const isCompany = entity.id === 'genlogic-co';
    const signed = !input.docInfo.hideSignatureFields && input.docInfo.isSigned;
    return {
        sellerInfo: {
            name: seller.name,
            branch: seller.branch,
            taxId: seller.taxId,
            website: seller.website,
            address: seller.address,
            email: seller.email,
        },
        customerInfo: {
            name: formatString(input.customerInfo.name),
            branch: formatString(input.customerInfo.branch),
            address: formatString(
                input.customerInfo.useCustomAddress
                    ? input.customerInfo.customAddress
                    : input.customerInfo.fullAddress,
            ),
            taxId: formatString(input.customerInfo.taxId),
        },
        orderInfo: {
            invNum: formatString(input.docInfo.invNum),
            orderNo: formatString(input.docInfo.refNo),
            date: formatDisplayDate(input.docInfo.date),
            remark: formatString(input.docInfo.remark),
            sellerSignDate: signed
                ? formatDisplayDate(input.docInfo.date, true)
                : '',
        },
        docInfo: {
            title1:
                formatString(input.docInfo.customTitle) ||
                TEMP_DELIVERY_NOTE_TITLE,
            hideSignatureFields: input.docInfo.hideSignatureFields,
            hideSellerSignature: !input.docInfo.isSigned || !isCompany,
            hideLogo: !isCompany,
        },
        items: _map<DeliveryItemInfo, DeliveryNoteItem>(
            input.items,
            (item: DeliveryItemInfo, index: number): DeliveryNoteItem => {
                return {
                    itemNo: index + 1,
                    description: formatString(item.description),
                    // null, not 0: a blank quantity leaves the cell empty
                    // rather than printing "0 ชิ้น", which would assert that
                    // nothing was delivered for that line.
                    quantity: _toNumber(item.quantity) || null,
                    quantityName: formatString(item.quantityName),
                };
            },
        ),
    };
};
